import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  Button,
  CreditTransactionDetailModal,
  FormField,
  Input,
  Modal,
  PageHeader,
  PortalUsdLedgerSummaryGrid,
  PortalUsdWalletFiltersBar,
  PortalUsdWalletLedgerTable,
  PortalUsdWalletSummaryGrid,
  computeUsdLedgerSummary,
  PortalWithdrawalLimitsPanel,
  PortalWithdrawalFeeLimitNotice,
  Select,
  Textarea,
  UsdWalletTransactionDetailModal,
  WithdrawBlockedAlert,
  WithdrawOtpStep,
  type WithdrawOtpChannel,
  defaultUsdLedgerFilters,
  downloadBlob,
  filterUsdLedgerRows,
  formatUsdPlain,
  resolveUsdPeriodDateRange,
  usePortalLedgerDetailState,
  validateUsdExportPeriod,
  type PortalWithdrawalLimitsSnapshot,
  type UsdLedgerFilterState,
  MONEY,
  PAGE,
} from '@kioskgaming/ui';
import type { SortDirection } from '@kioskgaming/ui/dist/data-table/sortState';
import AppLayout from '../components/layout/AppLayout';
import { getTableSortColumns } from '../config/tableSortConfig';
import { dispatchShopCreditBalanceRefresh } from '../constants';
import { useAuth } from '../hooks/useAuth';
import { useShopOnlineBalanceLedger } from '../hooks/useShopOnlineBalanceLedger';
import useTableSort from '../hooks/useTableSort';
import {
  createShopOnlineBalanceWithdrawal,
  exportShopOnlineBalanceLedger,
  fetchShopCreditTransactionById,
  fetchShopOnlineBalanceWithdrawalFee,
  fetchShopPortalWithdrawalLimits,
  fetchShopUsdLedgerEntryById,
  fetchShopWithdrawalOtpEligibility,
  sendShopWithdrawalOtp,
  verifyShopWithdrawalOtp,
  type OnlineDebtPayoutNetwork,
} from '../services/api';
import { computeNetWithdrawalFeePreview } from '../utils/netWithdrawalFee';

function openVerifyAccountFromHeader() {
  const btn = document.querySelector('[aria-label="Verify account"]');
  if (btn instanceof HTMLElement) {
    btn.click();
    return;
  }
  toast.error('Use Verify now in the top bar to verify your account.');
}

const PAGE_SIZE = 50;

export default function OnlineBalanceTransactionsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedUsd = searchParams.get('usd');
  const openedUsd = useRef<string | null>(null);
  const page = Math.max(1, Number.parseInt(searchParams.get('page') || '1', 10) || 1);
  const [draft, setDraft] = useState<UsdLedgerFilterState>(defaultUsdLedgerFilters);
  const [applied, setApplied] = useState<UsdLedgerFilterState>(defaultUsdLedgerFilters);
  const [exporting, setExporting] = useState(false);

  const tableSort = useTableSort({
    columns: getTableSortColumns('shopUsdLedger'),
    onChange: () => {
      const params = new URLSearchParams(searchParams);
      params.delete('page');
      setSearchParams(params, { replace: true });
    },
  });

  const { data, loading, reload } = useShopOnlineBalanceLedger({
    currency: 'USD',
    ...(applied.typeFilter !== 'all' ? { type: applied.typeFilter } : {}),
    page,
    limit: PAGE_SIZE,
    sortBy: tableSort.sortBy,
    sortOrder: tableSort.sortOrder,
  });

  const [withdrawAmountUsd, setWithdrawAmountUsd] = useState<string>('');
  const [withdrawNetwork, setWithdrawNetwork] = useState<OnlineDebtPayoutNetwork>('btc');
  const [withdrawAddress, setWithdrawAddress] = useState<string>('');
  const [withdrawNote, setWithdrawNote] = useState<string>('');
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [withdrawStep, setWithdrawStep] = useState<1 | 2>(1);
  const [withdrawChallengeToken, setWithdrawChallengeToken] = useState<string | null>(null);
  const [otpEligibility, setOtpEligibility] = useState<{
    emailVerified: boolean;
    phoneVerified: boolean;
    maskedEmail?: string | null;
    maskedPhone?: string | null;
  } | null>(null);
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);

  const contactVerified = !!(user?.emailVerified || user?.phoneVerified);
  const [withdrawFeeRates, setWithdrawFeeRates] = useState({
    system: 0,
    gateway: 0,
    blended: 0,
    networkType: 'none' as 'none' | 'percent' | 'fixed',
    networkRate: 0,
    networkFixedUsd: 0,
    feeConfigDisabled: false,
    feeConfigMessage: '',
  });
  const [withdrawFeeLoading, setWithdrawFeeLoading] = useState(false);
  const [portalLimits, setPortalLimits] = useState<PortalWithdrawalLimitsSnapshot | null>(null);
  const [portalLimitsLoading, setPortalLimitsLoading] = useState(true);

  const newIdempotencyKey = () => {
    try {
      return crypto.randomUUID();
    } catch {
      return `idemp_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    }
  };
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() => newIdempotencyKey());

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const filtered = useMemo(() => filterUsdLedgerRows(rows, applied), [rows, applied]);
  const summary = useMemo(() => {
    const computed = computeUsdLedgerSummary(filtered);
    if (data?.count != null) {
      return { ...computed, totalTransactions: data.count };
    }
    return computed;
  }, [filtered, data?.count]);
  const isGloballyEmpty = !loading && (data?.count ?? 0) === 0;
  const currentNet = Number(data?.balanceNet ?? 0);

  const appliedSort = useMemo(
    () => ({
      sortBy: null as string | null,
      sortOrder: null as SortDirection | null,
    }),
    [],
  );

  const headerSort = tableSort.sortBy
    ? {
        activeKey: appliedSort.sortBy ?? tableSort.sortBy,
        direction: appliedSort.sortOrder ?? tableSort.sortOrder,
      }
    : { activeKey: appliedSort.sortBy, direction: appliedSort.sortOrder };

  const ledgerDetail = usePortalLedgerDetailState({
    usdRows: rows,
    fetchCreditById: async (id) => {
      const res = await fetchShopCreditTransactionById(id);
      if (!res.success || !res.data?.item) throw new Error(res.message || 'Not found');
      return res.data.item;
    },
    fetchUsdById: async (id) => {
      const res = await fetchShopUsdLedgerEntryById(id);
      if (!res.success || !res.data?.item) throw new Error(res.message || 'Not found');
      return res.data.item;
    },
    onUnavailable: () =>
      toast.error('Related transaction is unavailable or outside your permission.'),
  });

  useEffect(() => {
    if (!linkedUsd) {
      openedUsd.current = null;
      return;
    }
    if (openedUsd.current === linkedUsd) return;
    openedUsd.current = linkedUsd;
    void ledgerDetail.openUsdById(linkedUsd);
  }, [linkedUsd, ledgerDetail.openUsdById]);

  const closeLinkedUsd = () => {
    ledgerDetail.closeUsd();
    if (!searchParams.get('usd')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('usd');
    setSearchParams(next, { replace: true });
  };

  const resetFilters = () => {
    const next = defaultUsdLedgerFilters();
    setDraft(next);
    setApplied(next);
    const params = new URLSearchParams(searchParams);
    params.delete('page');
    setSearchParams(params, { replace: true });
  };

  const applyFilters = () => {
    setApplied(draft);
    const params = new URLSearchParams(searchParams);
    params.delete('page');
    setSearchParams(params, { replace: true });
  };

  const handleExport = useCallback(async () => {
    const dates = resolveUsdPeriodDateRange(applied.period, applied.dateFrom, applied.dateTo);
    const periodErr = validateUsdExportPeriod(dates.dateFrom, dates.dateTo);
    if (periodErr) {
      toast.error(periodErr);
      return;
    }
    setExporting(true);
    try {
      const { blob, filename } = await exportShopOnlineBalanceLedger({
        currency: 'USD',
        ...(applied.typeFilter !== 'all' ? { type: applied.typeFilter } : {}),
        ...(applied.statusFilter !== 'all' ? { status: applied.statusFilter } : {}),
        ...(applied.query.trim() ? { query: applied.query.trim() } : {}),
        dateFrom: dates.dateFrom,
        dateTo: dates.dateTo,
      });
      downloadBlob(blob, filename);
      toast.success('USD wallet transactions exported');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [applied]);

  useEffect(() => {
    let cancelled = false;
    const loadFee = async () => {
      setWithdrawFeeLoading(true);
      try {
        const res = await fetchShopOnlineBalanceWithdrawalFee({ payoutNetwork: withdrawNetwork });
        const system = Number(res?.data?.systemFeeRate ?? 0);
        const gateway = Number(res?.data?.gatewayFeeRate ?? 0);
        const blended = Number(res?.data?.feeRate ?? 0);
        const networkType = String(res?.data?.networkFeeType || 'none').toLowerCase();
        const networkRate = Number(res?.data?.networkFeeRate ?? 0);
        const networkFixedUsd = Number(res?.data?.networkFeeAmount ?? 0);
        const feeConfigDisabled = Boolean(res?.data?.feeConfigDisabled);
        const feeConfigMessage =
          typeof res?.data?.feeConfigMessage === 'string' ? res.data.feeConfigMessage : '';
        if (!cancelled) {
          setWithdrawFeeRates({
            system: Number.isFinite(system) && system >= 0 ? system : 0,
            gateway: Number.isFinite(gateway) && gateway >= 0 ? gateway : 0,
            blended: Number.isFinite(blended) && blended >= 0 ? blended : 0,
            networkType:
              networkType === 'percent' || networkType === 'fixed' ? networkType : 'none',
            networkRate: Number.isFinite(networkRate) && networkRate >= 0 ? networkRate : 0,
            networkFixedUsd:
              Number.isFinite(networkFixedUsd) && networkFixedUsd >= 0 ? networkFixedUsd : 0,
            feeConfigDisabled,
            feeConfigMessage,
          });
        }
      } catch {
        if (!cancelled) {
          setWithdrawFeeRates({
            system: 0,
            gateway: 0,
            blended: 0,
            networkType: 'none',
            networkRate: 0,
            networkFixedUsd: 0,
            feeConfigDisabled: false,
            feeConfigMessage: '',
          });
        }
      } finally {
        if (!cancelled) setWithdrawFeeLoading(false);
      }
    };
    void loadFee();
    return () => {
      cancelled = true;
    };
  }, [withdrawNetwork]);

  const loadPortalLimits = async (amountUsd?: number) => {
    setPortalLimitsLoading(true);
    try {
      const res = await fetchShopPortalWithdrawalLimits(
        amountUsd != null && amountUsd > 0 ? { amountUsd } : undefined,
      );
      if (res.success && res.data) setPortalLimits(res.data);
    } catch {
      setPortalLimits(null);
    } finally {
      setPortalLimitsLoading(false);
    }
  };

  const withdrawNet = useMemo(() => {
    const n = Number(withdrawAmountUsd);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [withdrawAmountUsd]);

  useEffect(() => {
    void loadPortalLimits();
  }, []);

  useEffect(() => {
    if (!withdrawModalOpen) return;
    const t = window.setTimeout(() => {
      void loadPortalLimits(withdrawNet > 0 ? withdrawNet : undefined);
    }, 300);
    return () => window.clearTimeout(t);
  }, [withdrawModalOpen, withdrawNet]);

  const withdrawFeeBreakdown = useMemo(() => {
    if (withdrawNet <= 0) return null;
    const systemRate =
      withdrawFeeRates.system > 0 || withdrawFeeRates.gateway > 0
        ? withdrawFeeRates.system
        : withdrawFeeRates.blended;
    return computeNetWithdrawalFeePreview(
      withdrawNet,
      systemRate,
      withdrawFeeRates.gateway,
      {
        type: withdrawFeeRates.networkType,
        rate: withdrawFeeRates.networkRate,
        fixedUsd: withdrawFeeRates.networkFixedUsd,
      },
      0,
    );
  }, [withdrawNet, withdrawFeeRates]);

  const withdrawFeeUsd = withdrawFeeBreakdown?.fee ?? 0;
  const withdrawTotalDebit = withdrawFeeBreakdown?.tierFunding ?? withdrawNet;
  const isOverBalance = useMemo(() => withdrawTotalDebit - currentNet > 1e-9, [withdrawTotalDebit, currentNet]);
  const exceedsPortalLimits = useMemo(() => {
    if (!portalLimits || withdrawNet <= 0) return false;
    return (
      withdrawNet > portalLimits.maxWithdrawalPerTransaction + 1e-9 ||
      withdrawTotalDebit > portalLimits.remainingTodayUsd + 1e-9
    );
  }, [portalLimits, withdrawNet, withdrawTotalDebit]);
  const dailyLimitReached = !!portalLimits?.dailyLimitReached;

  const openWithdrawModal = () => {
    if (!contactVerified) {
      toast.error('Verify your email or phone before withdrawing.');
      return;
    }
    setWithdrawStep(1);
    setWithdrawChallengeToken(null);
    setWithdrawModalOpen(true);
    void fetchShopWithdrawalOtpEligibility().then((res) => {
      if (res.success && res.data) {
        setOtpEligibility({
          emailVerified: res.data.emailVerified,
          phoneVerified: res.data.phoneVerified,
          maskedEmail: res.data.maskedEmail,
          maskedPhone: res.data.maskedPhone,
        });
      }
    });
  };

  const closeWithdrawModal = () => {
    if (withdrawSubmitting) return;
    setWithdrawModalOpen(false);
    setWithdrawStep(1);
    setWithdrawChallengeToken(null);
  };

  const goToOtpStep = () => {
    const amt = Number(withdrawAmountUsd);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Invalid amount');
      return;
    }
    if (!withdrawAddress.trim()) {
      toast.error('Wallet address is required');
      return;
    }
    if (isOverBalance) {
      toast.error('Amount + fee exceeds current USD balance');
      return;
    }
    if (exceedsPortalLimits || dailyLimitReached) {
      toast.error('Amount exceeds withdrawal limits');
      return;
    }
    setWithdrawStep(2);
    setWithdrawChallengeToken(null);
  };

  const submitWithdrawal = async () => {
    const amt = Number(withdrawAmountUsd);
    if (!withdrawChallengeToken) {
      toast.error('Complete security verification first');
      return;
    }
    setWithdrawSubmitting(true);
    try {
      const res = await createShopOnlineBalanceWithdrawal({
        amountUsd: amt,
        payoutNetwork: withdrawNetwork,
        payoutWalletAddress: withdrawAddress.trim(),
        note: withdrawNote.trim() ? withdrawNote.trim() : null,
        idempotencyKey,
        withdrawChallengeToken,
      });
      if (!res.success) {
        toast.error(res.message || 'Payout request failed');
        return;
      }
      toast.success('Payout submitted — funds are being sent to your wallet');
      setWithdrawAmountUsd('');
      setWithdrawNote('');
      setIdempotencyKey(newIdempotencyKey());
      closeWithdrawModal();
      dispatchShopCreditBalanceRefresh();
      await reload();
      void loadPortalLimits();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      toast.error(err?.response?.data?.message || err?.message || 'Payout request failed');
    } finally {
      setWithdrawSubmitting(false);
    }
  };

  return (
    <AppLayout contentFullWidth>
      <div className="space-y-6">
        <PageHeader
          title={PAGE.usdWalletTransactions}
          description="Track USD wallet activity, balances and payouts."
          className="!gap-3"
        />

        {!contactVerified ? (
          <WithdrawBlockedAlert className="max-w-3xl" onVerifyClick={openVerifyAccountFromHeader} />
        ) : null}

        <PortalUsdWalletSummaryGrid
          balanceNet={currentNet}
          limits={portalLimits}
          limitsLoading={portalLimitsLoading}
          onRequestWithdrawal={openWithdrawModal}
          withdrawDisabled={withdrawSubmitting || dailyLimitReached || !contactVerified}
          withdrawLoading={withdrawSubmitting}
        />

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-gray-100 px-4 py-4 sm:flex-row sm:items-start sm:justify-between sm:px-6">
            <PortalUsdLedgerSummaryGrid summary={summary} loading={loading} />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => void handleExport()}
              disabled={exporting || loading}
            >
              <Download className={`mr-2 h-4 w-4 ${exporting ? 'animate-pulse' : ''}`} />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          </div>
          <PortalUsdWalletFiltersBar
            draft={draft}
            onDraftChange={(patch) => setDraft((prev) => ({ ...prev, ...patch }))}
            onApply={applyFilters}
            onReset={resetFilters}
          />
          <PortalUsdWalletLedgerTable
            rows={filtered}
            loading={loading}
            isGloballyEmpty={isGloballyEmpty}
            onViewDetail={(row) => {
              const id = row.id != null ? String(row.id) : '';
              if (id) void ledgerDetail.openUsdById(id);
            }}
            sortable
            sortActiveKey={headerSort.activeKey}
            sortDirection={headerSort.direction}
            onSort={tableSort.toggleSort}
          />
        </div>
      </div>

      <Modal
        isOpen={withdrawModalOpen}
        onClose={closeWithdrawModal}
        title={MONEY.requestPayout}
        subtitle={
          withdrawStep === 1
            ? 'Deducts balance immediately and submits a crypto payout (BTC via BTCPay, ETH/USDT via 0x).'
            : 'Enter the code we send to your verified email or phone.'
        }
        variant="form"
        size="lg"
        primaryAction={{
          label: withdrawStep === 1 ? 'Continue' : 'Verify & submit',
          onClick: () => void (withdrawStep === 1 ? goToOtpStep() : submitWithdrawal()),
          loading: withdrawSubmitting,
          disabled:
            withdrawSubmitting ||
            (withdrawStep === 1 &&
              (withdrawFeeLoading ||
                !withdrawAmountUsd.trim() ||
                !withdrawAddress.trim() ||
                isOverBalance ||
                exceedsPortalLimits ||
                dailyLimitReached)) ||
            (withdrawStep === 2 && !withdrawChallengeToken),
        }}
        secondaryAction={{
          label: withdrawStep === 1 ? 'Cancel' : 'Back',
          onClick: () => (withdrawStep === 1 ? closeWithdrawModal() : setWithdrawStep(1)),
        }}
        closeOnOverlayClick={!withdrawSubmitting}
        preventClose={withdrawSubmitting}
      >
        <div className="mb-4 flex gap-2 text-xs font-medium text-gray-500" aria-label="Withdrawal steps">
          <span className={withdrawStep === 1 ? 'text-emerald-700' : ''} aria-current={withdrawStep === 1 ? 'step' : undefined}>
            1. Withdrawal details
          </span>
          <span aria-hidden>—</span>
          <span className={withdrawStep === 2 ? 'text-emerald-700' : ''} aria-current={withdrawStep === 2 ? 'step' : undefined}>
            2. Security verification
          </span>
        </div>

        {withdrawStep === 2 ? (
          <WithdrawOtpStep
            emailVerified={otpEligibility?.emailVerified ?? !!user?.emailVerified}
            phoneVerified={otpEligibility?.phoneVerified ?? !!user?.phoneVerified}
            maskedEmail={otpEligibility?.maskedEmail}
            maskedPhone={otpEligibility?.maskedPhone}
            summaryLine={`$${withdrawNet.toFixed(2)} net · ${withdrawNetwork.toUpperCase()} · ${withdrawAddress.trim().slice(0, 8)}…`}
            disabled={withdrawSubmitting}
            onSendOtp={async (channel: WithdrawOtpChannel) => {
              const res = await sendShopWithdrawalOtp({ channel });
              if (!res.success) throw new Error(res.message || 'Failed to send code');
              return { nextResendAt: res.data?.next_resend_at ?? null };
            }}
            onVerifyOtp={async (channel, otp) => {
              const res = await verifyShopWithdrawalOtp({ channel, otp });
              if (!res.success || !res.data?.withdrawChallengeToken) {
                throw new Error(res.message || 'Verification failed');
              }
              return { withdrawChallengeToken: res.data.withdrawChallengeToken };
            }}
            onChallengeReady={(token) => setWithdrawChallengeToken(token)}
            onError={(msg) => toast.error(msg)}
          />
        ) : (
        <div className="space-y-4">
          <FormField label="Amount to receive (USD)" required state={withdrawSubmitting ? 'disabled' : 'default'}>
            <Input
              inputMode="decimal"
              placeholder="e.g. 25"
              value={withdrawAmountUsd}
              onChange={(e) => setWithdrawAmountUsd(e.target.value)}
              disabled={withdrawSubmitting}
            />
          </FormField>

            <PortalWithdrawalLimitsPanel
              limits={portalLimits}
              loading={portalLimitsLoading}
              amountUsd={withdrawNet}
              limitUsageUsd={withdrawTotalDebit}
              variant="modal"
            />

            <PortalWithdrawalFeeLimitNotice
              variant="modal"
              netUsd={withdrawNet}
              feeUsd={withdrawFeeUsd}
              limitUsageUsd={withdrawTotalDebit}
            />

          <div className="rounded-lg border border-stone-200 bg-white p-3 text-sm text-stone-700">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wide text-stone-500">Fee</div>
                <div className="mt-1 font-semibold tabular-nums text-stone-900">
                  {withdrawFeeLoading
                    ? '…'
                    : `${((withdrawFeeRates.system + withdrawFeeRates.gateway) * 100).toFixed(2)}%`}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs font-semibold uppercase tracking-wide text-stone-500">Fee (USD)</div>
                <div className="mt-1 font-semibold tabular-nums text-stone-900">${withdrawFeeUsd.toFixed(2)}</div>
              </div>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-lg bg-stone-50 px-2 py-2">
                <div className="text-stone-500">You will receive</div>
                <div className="mt-0.5 font-semibold tabular-nums text-stone-900">${withdrawNet.toFixed(2)}</div>
              </div>
              <div className="rounded-lg bg-stone-50 px-2 py-2 text-right">
                <div className="text-stone-500">Total deducted</div>
                <div
                  className={`mt-0.5 font-semibold tabular-nums ${isOverBalance ? 'text-rose-700' : 'text-stone-900'}`}
                >
                  ${withdrawTotalDebit.toFixed(2)}
                </div>
              </div>
              <div className="rounded-lg bg-stone-50 px-2 py-2">
                <div className="text-stone-500">System + gate</div>
                <div className="mt-0.5 font-semibold tabular-nums text-stone-900">
                  ${((withdrawFeeBreakdown?.systemFee ?? 0) + (withdrawFeeBreakdown?.gatewayFee ?? 0)).toFixed(2)}
                </div>
              </div>
              <div className="rounded-lg bg-stone-50 px-2 py-2 text-right">
                <div className="text-stone-500">Network fee</div>
                <div className="mt-0.5 font-semibold tabular-nums text-stone-900">
                  ${(withdrawFeeBreakdown?.networkFee ?? 0).toFixed(2)}
                </div>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="text-stone-500">{MONEY.usdWalletBalance}</span>
              <span className="font-semibold tabular-nums text-stone-900">{formatUsdPlain(currentNet)}</span>
            </div>
            {isOverBalance ? (
              <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50 px-2 py-2 text-xs text-rose-800">
                Amount + fee must be less than or equal to your current USD balance.
              </div>
            ) : null}
            {withdrawFeeRates.feeConfigDisabled ? (
              <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2 py-2 text-xs text-amber-800">
                {withdrawFeeRates.feeConfigMessage ||
                  'Fee configuration for this network is disabled. No fee will be applied.'}
              </div>
            ) : null}
          </div>

          <FormField label="Network" required state={withdrawSubmitting ? 'disabled' : 'default'}>
            <Select
              value={withdrawNetwork}
              onChange={(e) => setWithdrawNetwork(e.target.value as OnlineDebtPayoutNetwork)}
              disabled={withdrawSubmitting}
            >
              <option value="btc">BTC - BTCPay / Bitcoin On-Chain</option>
              <option value="eth">ETH - 0xProcessing / ETH</option>
              <option value="usdt_erc">USDT ERC20 - 0xProcessing / USDT (ERC20)</option>
            </Select>
            <p className="mt-1 text-xs text-stone-500">
              Fees are loaded from Fee configuration for the selected provider and withdrawal method.
            </p>
          </FormField>

          <FormField label="Wallet address" required state={withdrawSubmitting ? 'disabled' : 'default'}>
            <Input
              placeholder="Destination address…"
              value={withdrawAddress}
              onChange={(e) => setWithdrawAddress(e.target.value)}
              disabled={withdrawSubmitting}
            />
          </FormField>

          <FormField label="Note (optional)" state={withdrawSubmitting ? 'disabled' : 'default'}>
            <Textarea
              placeholder="Optional…"
              value={withdrawNote}
              onChange={(e) => setWithdrawNote(e.target.value)}
              disabled={withdrawSubmitting}
              showCharacterCount={false}
            />
          </FormField>
        </div>
        )}
      </Modal>
      <UsdWalletTransactionDetailModal
        row={ledgerDetail.usdDetail}
        onClose={closeLinkedUsd}
        onOpenCreditSource={ledgerDetail.handleOpenCreditSource}
        onOpenUsdSource={ledgerDetail.handleOpenUsdSource}
        relatedLoading={ledgerDetail.relatedLoading}
      />
      <CreditTransactionDetailModal
        entry={ledgerDetail.creditDetail}
        onClose={ledgerDetail.closeCredit}
        accountLabel="Your Shop account"
        audience="operator"
        onOpenUsdSource={ledgerDetail.handleOpenUsdSource}
        relatedLoading={ledgerDetail.relatedLoading}
        resolveUsdConversion={ledgerDetail.resolveUsdConversion}
      />
    </AppLayout>
  );
}
